import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useNavigate } from '@tanstack/react-router';
import { Plus, Trash2, AlertCircle, ChevronRight, ChevronDown, Pencil, Loader2, Files } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { GradientCard } from '@/components/ui/gradient-card';
import { GradientH2 } from '@/components/ui/gradient-text';
import { PageLoader } from '@/components/ui/loader';
import { DocumentList } from '@/components/documents';
import { casesAPI } from '../utils/api';

const CaseList = () => {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModalOpened, setDeleteModalOpened] = useState(false);
  const [caseToDelete, setCaseToDelete] = useState(null);
  const [deletingCaseId, setDeletingCaseId] = useState(null);

  const fetchCases = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await casesAPI.getCases();

      if (result.status === 'success') {
        setCases(result.cases || []);
      }
    } catch (err) {
      console.error('Error fetching cases:', err);
      setError('Failed to load cases. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCases();
  }, []);

  const handleCreateCase = async () => {
    // Navigate directly to new claim flow - case creation happens after booking review
    navigate({ to: '/client/new-claim' });
  };

  const handleContinueCase = (caseItem) => {
    // All draft cases now use the new claim flow
    navigate({ to: '/client/new-claim', search: { resumeCase: caseItem.id } });
  };

  const handleDeleteClick = (caseItem) => {
    setCaseToDelete(caseItem);
    setDeleteModalOpened(true);
  };

  const handleDeleteConfirm = async () => {
    if (!caseToDelete) return;

    try {
      setDeletingCaseId(caseToDelete.id);
      const result = await casesAPI.deleteCase(caseToDelete.id);

      if (result.status === 'success') {
        toast.success(result.message || 'Case deleted successfully');
        setCases(cases.filter(c => c.id !== caseToDelete.id));
      }
    } catch (error) {
      console.error('Error deleting case:', error);
      toast.error('Failed to delete case. Please try again.');
    } finally {
      setDeletingCaseId(null);
      setDeleteModalOpened(false);
      setCaseToDelete(null);
    }
  };

  const getIncidentTypeBadgeVariant = (type) => {
    switch (type) {
      case 'delay':
        return 'warning';
      case 'cancel':
        return 'error';
      case 'denied_boarding':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  const getIncidentTypeLabel = (type) => {
    switch (type) {
      case 'delay':
        return 'Flight Delay';
      case 'cancel':
        return 'Cancellation';
      case 'denied_boarding':
        return 'Denied Boarding';
      default:
        return type;
    }
  };

  const getStateBadgeVariant = (state) => {
    switch (state) {
      case 'draft':
        return 'warning';
      case 'submitted':
        return 'info';
      case 'assigned':
        return 'secondary';
      case 'in_progress':
        return 'secondary';
      case 'resolved':
        return 'success';
      case 'closed':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  const getStateLabel = (caseItem) => {
    // For draft cases, show wizard progress
    if (caseItem.state === 'draft') {
      return 'Draft';
    }
    // Use state_display from backend if available
    return caseItem.state_display || caseItem.state;
  };

  if (loading) {
    return <PageLoader />;
  }

  if (error) {
    return (
      <Alert variant="destructive" className="mb-4">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <GradientH2>My Cases</GradientH2>
          {cases.length > 0 && (
            <Button onClick={handleCreateCase}>
              <Plus className="mr-2 h-4 w-4" />
              Start New Claim
            </Button>
          )}
        </div>

        {/* Empty state */}
        {cases.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-lg text-slate-500">No cases yet</p>
            <p className="text-sm text-slate-400 mt-2 mb-6">
              Start your first claim to get compensation for flight disruptions
            </p>
            <Button size="lg" onClick={handleCreateCase}>
              <Plus className="mr-2 h-4 w-4" />
              Start My Claim
            </Button>
          </div>
        ) : (
          /* Case cards grid */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {cases.map((caseItem) => {
              // Use is_draft from backend (checks state === 'draft')
              const isDraft = caseItem.is_draft;

              return (
                <GradientCard
                  key={caseItem.id}
                  className="cursor-pointer hover:shadow-2xl transition-shadow duration-200"
                  onClick={() => isDraft
                    ? handleContinueCase(caseItem)
                    : navigate({ to: `/client/case/${caseItem.id}` })
                  }
                >
                  <div className="flex flex-col gap-3">
                    {/* Header with case number and state badge */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-lg text-slate-800">
                          Case {caseItem.case_number}
                        </span>
                        <ChevronRight className="h-4 w-4 text-slate-400" />
                      </div>
                      <Badge variant={getStateBadgeVariant(caseItem.state)}>
                        {getStateLabel(caseItem)}
                      </Badge>
                    </div>

                    {/* Incident type or incomplete message */}
                    {caseItem.incident_type ? (
                      <Badge
                        variant={getIncidentTypeBadgeVariant(caseItem.incident_type)}
                        className="w-fit text-sm px-3 py-1"
                      >
                        {getIncidentTypeLabel(caseItem.incident_type)}
                      </Badge>
                    ) : isDraft && (
                      <p className="text-sm text-slate-400 italic">
                        Incomplete - please continue to add details
                      </p>
                    )}

                    {/* Case details */}
                    {caseItem.airline_code && (
                      <p className="text-sm text-slate-500">
                        Airline: <span className="font-medium text-slate-700">{caseItem.airline_code}</span>
                      </p>
                    )}

                    {caseItem.incident_date && (
                      <p className="text-sm text-slate-500">
                        Date: <span className="font-medium text-slate-700">{caseItem.incident_date}</span>
                      </p>
                    )}

                    <p className="text-xs text-slate-400">
                      Created: {new Date(caseItem.created_at).toLocaleDateString()}
                    </p>

                    {/* Documents section */}
                    {caseItem.documents && caseItem.documents.length > 0 && (
                      <Collapsible className="mt-2" onClick={(e) => e.stopPropagation()}>
                        <CollapsibleTrigger asChild>
                          <button className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 transition-colors">
                            <Files className="h-3.5 w-3.5" />
                            <span>{caseItem.document_count} document{caseItem.document_count !== 1 ? 's' : ''}</span>
                            <ChevronDown className="h-3 w-3 transition-transform duration-200 [[data-state=open]>&]:rotate-180" />
                          </button>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="mt-2">
                          <DocumentList
                            documents={caseItem.documents}
                            compact={true}
                            showDelete={false}
                          />
                        </CollapsibleContent>
                      </Collapsible>
                    )}

                    {/* Action buttons */}
                    <div className="flex gap-2 pt-2" onClick={(e) => e.stopPropagation()}>
                      {isDraft ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleContinueCase(caseItem);
                          }}
                        >
                          <Pencil className="mr-2 h-3 w-3" />
                          Continue
                        </Button>
                      ) : (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate({ to: `/client/case/${caseItem.id}` });
                          }}
                        >
                          <ChevronRight className="mr-2 h-3 w-3" />
                          View
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(caseItem);
                        }}
                        disabled={deletingCaseId === caseItem.id}
                      >
                        {deletingCaseId === caseItem.id ? (
                          <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                        ) : (
                          <Trash2 className="mr-2 h-3 w-3" />
                        )}
                        Delete
                      </Button>
                    </div>
                  </div>
                </GradientCard>
              );
            })}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteModalOpened} onOpenChange={setDeleteModalOpened}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete case{' '}
              <span className="font-bold">{caseToDelete?.case_number}</span>?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModalOpened(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CaseList;
